import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Users, Plus, Search, Building, Briefcase, Phone, Mail, 
  Edit, Trash2, RefreshCw, Loader2, User, Calendar, MapPin,
  Filter, Download, UserPlus, UserX, UserCheck, AlertTriangle, Eye, UserCog, Award, QrCode, Bug, CheckCircle, KeyRound
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AddEmployeeModal from '../components/AddEmployeeModal';
import { PhoneInput } from '../components/ui/phone-input';
import BadgeManagementModal, { EmployeeBadges } from '../components/BadgeManagement';
import { EmployeeQRCode, EmployeeQRModal } from '../components/EmployeeQRCode';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';

const DEACTIVATION_REASONS = [
  { value: 'Resigned', label: 'Resigned', description: 'Employee voluntarily left' },
  { value: 'Terminated', label: 'Terminated', description: 'Employment was terminated' },
  { value: 'Left the company', label: 'Left the company', description: 'General departure' },
  { value: 'Contract ended', label: 'Contract ended', description: 'Employment contract expired' },
  { value: 'Retired', label: 'Retired', description: 'Employee retired' },
  { value: 'Other', label: 'Other', description: 'Other reason' },
];

const Employees = ({ searchQuery = '' }) => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Deactivation modal state
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [deactivationReason, setDeactivationReason] = useState('');
  const [deactivationDetails, setDeactivationDetails] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Line Manager modal state
  const [showLineManagerModal, setShowLineManagerModal] = useState(false);
  const [lineManagerSearch, setLineManagerSearch] = useState('');
  
  // QR Code modal state
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedLineManager, setSelectedLineManager] = useState('');
  
  // Badge management modal state
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  
  // PIN Reset state
  const [resettingPinId, setResettingPinId] = useState(null);
  const [pinResetTarget, setPinResetTarget] = useState(null);
  
  // Debug state
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);

  const { user, getAuthHeaders, currentOffice, isOfficeAdmin, isSuperAdmin } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Helper to get office ID from currentOffice or user's role
  const getOfficeId = () => {
    // Priority 1: Current office from context
    if (currentOffice?.id) {
      console.log('[Employees] Using currentOffice.id:', currentOffice.id);
      return currentOffice.id;
    }
    // Priority 2: For office admins, get office ID from their role
    if (user?.roles) {
      const officeAdminRole = user.roles.find(r => r.role === 'office_admin' && r.office_id);
      if (officeAdminRole) {
        console.log('[Employees] Using office admin role office_id:', officeAdminRole.office_id);
        return officeAdminRole.office_id;
      }
    }
    console.log('[Employees] No office ID found. currentOffice:', currentOffice, 'user.roles:', user?.roles);
    return null;
  };

  const fetchEmployees = async () => {
    const officeId = getOfficeId();
    console.log('[Employees] fetchEmployees called with officeId:', officeId);
    
    try {
      const headers = getAuthHeaders();
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }

      const response = await fetch(`${API_URL}/api/employees/`, {
        headers
      });
      
      console.log('[Employees] API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[Employees] Fetched employees count:', data.length);
        setEmployees(data);
      } else {
        const errorText = await response.text();
        console.error('[Employees] API error:', errorText);
      }
    } catch (error) {
      console.error('[Employees] Fetch error:', error);
    }
  };

  // Debug function to check employee visibility issues
  const runDebugCheck = async () => {
    setDebugLoading(true);
    try {
      const headers = getAuthHeaders();
      const officeId = getOfficeId();
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }
      
      const response = await fetch(`${API_URL}/api/employees/debug-visibility`, { headers });
      if (response.ok) {
        const data = await response.json();
        setDebugData(data);
        setShowDebugModal(true);
        
        if (data.issues_detected?.length > 0) {
          toast.error(`Found ${data.issues_detected.length} issues!`);
        } else {
          toast.success('No issues detected');
        }
      } else {
        toast.error('Debug check failed');
      }
    } catch (error) {
      console.error('Debug check error:', error);
      toast.error(`Debug error: ${error.message}`);
    } finally {
      setDebugLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const headers = getAuthHeaders();
      const officeId = currentOffice?.id || user?.roles?.find(r => r.office_id)?.office_id;
      if (officeId) headers['X-Office-ID'] = officeId;

      // Primary: fetch from office_departments (created via Office Configuration)
      const configRes = await fetch(`${API_URL}/api/departments/?office_id=${officeId}`, { headers });
      if (configRes.ok) {
        const data = await configRes.json();
        if (data.length > 0) {
          setDepartments(data);
          return;
        }
      }

      // Fallback: fetch from employees/departments endpoint
      const response = await fetch(`${API_URL}/api/employees/departments/`, { headers });
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setDepartments(data);
          return;
        }
      }
      // Last resort: extract unique departments from existing employees
      const listResp = await fetch(`${API_URL}/api/employees/departments/list`, { headers });
      if (listResp.ok) {
        const listData = await listResp.json();
        setDepartments(listData);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchEmployees(), fetchDepartments()]);
      setLoading(false);
    };
    
    // Check if user is an office admin (look at their roles directly)
    const userIsOfficeAdmin = user?.roles?.some(r => r.role === 'office_admin' && r.office_id);
    const userIsSuperAdmin = user?.roles?.some(r => r.role === 'super_admin');
    
    // Get the office ID that would be used
    const officeId = getOfficeId();
    
    console.log('[Employees useEffect] Checking conditions:', {
      currentOffice: currentOffice?.id,
      userIsOfficeAdmin,
      userIsSuperAdmin,
      resolvedOfficeId: officeId,
      userRoles: user?.roles
    });
    
    // Load data if:
    // 1. We have a resolved office ID (from currentOffice or user role), OR
    // 2. User is a super admin (can see all employees)
    if (officeId || userIsSuperAdmin) {
      loadData();
    } else if (user !== null && !userIsOfficeAdmin && !userIsSuperAdmin && !currentOffice) {
      // User is loaded but doesn't have access - stop loading spinner
      console.log('[Employees useEffect] User loaded but no office access');
      setLoading(false);
    }
  }, [currentOffice, user]);

  // Update local search when prop changes
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Client-side filtering for better UX
  const filteredEmployees = employees.filter(emp => {
    // Search filter - check name, employee ID, email, phone
    const searchLower = localSearchQuery.toLowerCase().trim();
    const matchesSearch = !searchLower || 
      emp.first_name?.toLowerCase().includes(searchLower) ||
      emp.last_name?.toLowerCase().includes(searchLower) ||
      emp.full_name?.toLowerCase().includes(searchLower) ||
      emp.employment?.employee_id?.toLowerCase().includes(searchLower) ||
      emp.contact?.email?.toLowerCase().includes(searchLower) ||
      emp.contact?.phone?.includes(searchLower);
    
    // Department filter
    const matchesDepartment = selectedDepartment === 'all' || 
      emp.employment?.department === selectedDepartment;
    
    // Status filter
    const matchesStatus = selectedStatus === 'all' || 
      emp.employment?.employment_status === selectedStatus ||
      (selectedStatus === 'Inactive' && !emp.is_active);
    
    return matchesSearch && matchesDepartment && matchesStatus;
  });

  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      const officeId = getOfficeId();
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }

      const response = await fetch(`${API_URL}/api/employees/${employeeId}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        await fetchEmployees();
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
    }
  };

  const openDeactivateModal = (employee) => {
    setSelectedEmployee(employee);
    setDeactivationReason('');
    setDeactivationDetails('');
    setEffectiveDate(new Date().toISOString().split('T')[0]);
    setShowDeactivateModal(true);
  };

  const openActivateModal = (employee) => {
    setSelectedEmployee(employee);
    setShowActivateModal(true);
  };

  const openEditModal = (employee) => {
    setSelectedEmployee(employee);
    setShowEditModal(true);
  };

  const openViewModal = (employee) => {
    setSelectedEmployee(employee);
    setShowViewModal(true);
  };

  const handleResetPin = (employee) => {
    setPinResetTarget(employee);
  };

  const confirmResetPin = async () => {
    const employee = pinResetTarget;
    setPinResetTarget(null);
    if (!employee) return;
    const userId = employee.user_id;
    if (!userId) {
      toast.error('This employee does not have a linked user account');
      return;
    }
    setResettingPinId(employee.id);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const response = await fetch(`${API_URL}/api/auth/admin-reset-pin/${userId}`, {
        method: 'POST',
        headers,
      });
      if (response.ok) {
        toast.success(`PIN reset for ${employee.first_name} ${employee.last_name}. They will be prompted to set a new PIN on next login.`);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to reset PIN');
      }
    } catch {
      toast.error('Failed to reset PIN');
    } finally {
      setResettingPinId(null);
    }
  };


  const handleDeactivate = async () => {
    if (!selectedEmployee || !deactivationReason) return;
    
    setProcessing(true);
    try {
      const headers = getAuthHeaders();
      const officeId = getOfficeId();
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }

      const response = await fetch(`${API_URL}/api/employees/${selectedEmployee.id}/status`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          is_active: false,
          reason: deactivationReason,
          reason_details: deactivationDetails || null,
          effective_date: effectiveDate || null
        })
      });

      if (response.ok) {
        setShowDeactivateModal(false);
        await fetchEmployees();
      }
    } catch (error) {
      console.error('Error deactivating employee:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleActivate = async () => {
    if (!selectedEmployee) return;
    
    setProcessing(true);
    try {
      const headers = getAuthHeaders();
      const officeId = getOfficeId();
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }

      const response = await fetch(`${API_URL}/api/employees/${selectedEmployee.id}/status`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          is_active: true
        })
      });

      if (response.ok) {
        setShowActivateModal(false);
        await fetchEmployees();
      }
    } catch (error) {
      console.error('Error activating employee:', error);
    } finally {
      setProcessing(false);
    }
  };

  const openLineManagerModal = (employee) => {
    setSelectedEmployee(employee);
    setSelectedLineManager(employee.line_manager_id || '');
    setShowLineManagerModal(true);
  };

  const handleAssignLineManager = async () => {
    if (!selectedEmployee) return;
    
    setProcessing(true);
    try {
      const headers = getAuthHeaders();
      const officeId = getOfficeId();
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }

      const managerId = selectedLineManager === 'none' ? null : selectedLineManager;

      const response = await fetch(`${API_URL}/api/employees/${selectedEmployee.id}/line-manager`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          line_manager_id: managerId || null
        })
      });

      if (response.ok) {
        setShowLineManagerModal(false);
        await fetchEmployees();
      } else {
        const error = await response.json();
        console.error('Error assigning line manager:', error);
      }
    } catch (error) {
      console.error('Error assigning line manager:', error);
    } finally {
      setProcessing(false);
    }
  };

  // Get list of potential line managers (active employees excluding the selected one)
  const getPotentialManagers = () => {
    const search = lineManagerSearch.toLowerCase().trim();
    return employees
      .filter(emp => 
        emp.is_active && 
        emp.id !== selectedEmployee?.id &&
        (!search || 
          emp.first_name?.toLowerCase().includes(search) ||
          emp.last_name?.toLowerCase().includes(search) ||
          emp.full_name?.toLowerCase().includes(search) ||
          emp.employment?.job_title?.toLowerCase().includes(search) ||
          emp.employment?.department?.toLowerCase().includes(search) ||
          emp.employment?.employee_id?.toLowerCase().includes(search) ||
          emp.contact?.phone?.includes(search)
        )
      )
      .sort((a, b) => {
        // Sort by department, then by name
        const deptA = a.employment?.department || 'zzz';
        const deptB = b.employment?.department || 'zzz';
        if (deptA !== deptB) return deptA.localeCompare(deptB);
        return (a.full_name || '').localeCompare(b.full_name || '');
      });
  };

  // Get unique departments from potential managers
  const getManagerDepartments = () => {
    const managers = employees.filter(emp => emp.is_active && emp.id !== selectedEmployee?.id);
    const depts = [...new Set(managers.map(m => m.employment?.department).filter(Boolean))];
    return depts.sort();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'On Leave':
        return <Badge className="bg-yellow-100 text-yellow-800">On Leave</Badge>;
      case 'Terminated':
        return <Badge className="bg-red-100 text-red-800">Terminated</Badge>;
      case 'Resigned':
        return <Badge className="bg-gray-100 text-gray-800">Resigned</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="employees-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check if user has an office admin role with office_id
  const userHasOfficeAdminAccess = user?.roles?.some(r => r.role === 'office_admin' && r.office_id);
  
  if (!currentOffice && !isSuperAdmin() && !userHasOfficeAdminAccess) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="no-office-message">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Office Assigned</h3>
            <p className="text-gray-600">Please contact your administrator to assign you to an office.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Employees</h1>
          <p className="text-gray-600 dark:text-gray-400">
            {currentOffice ? `Manage employees for ${currentOffice.name}` : 'Manage all employees'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setLoading(true);
              fetchEmployees().finally(() => setLoading(false));
            }}
            title="Refresh employee list"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          {isSuperAdmin() && (
            <Button
              variant="outline"
              onClick={runDebugCheck}
              disabled={debugLoading}
              title="Debug employee visibility issues"
            >
              {debugLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Bug className="h-4 w-4 mr-2" />
              )}
              Debug
            </Button>
          )}
          <Button
            onClick={() => setShowAddModal(true)}
            data-testid="add-employee-button"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add Employee
          </Button>
        </div>
      </div>

      {/* Debug Info Alert - shows when employees array is unexpectedly empty */}
      {employees.length === 0 && !loading && (
        <Alert className="border-orange-300 bg-orange-50">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            <strong>No employees loaded.</strong> Office ID being used: <code className="bg-orange-100 px-1 rounded">{getOfficeId() || 'None'}</code>
            {!getOfficeId() && (
              <span className="block mt-1 text-sm">
                No office ID found. This might be why employees aren't showing. 
                Try clicking the Refresh button or contact support if the issue persists.
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Employees</p>
                <p className="text-2xl font-bold">{employees.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <UserCheck className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold">
                  {employees.filter(e => e.is_active && e.employment?.employment_status === 'Active').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Calendar className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">On Leave</p>
                <p className="text-2xl font-bold">
                  {employees.filter(e => e.employment?.employment_status === 'On Leave').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <UserX className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Inactive</p>
                <p className="text-2xl font-bold">
                  {employees.filter(e => !e.is_active).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, ID, email, phone..."
                className="pl-9"
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
                data-testid="search-employees-input"
              />
            </div>
            <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
              <SelectTrigger className="w-full sm:w-48" data-testid="department-filter">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="On Leave">On Leave</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
                <SelectItem value="Terminated">Terminated</SelectItem>
                <SelectItem value="Resigned">Resigned</SelectItem>
              </SelectContent>
            </Select>
            {/* Clear filters button */}
            {(localSearchQuery || selectedDepartment !== 'all' || selectedStatus !== 'all') && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setLocalSearchQuery('');
                  setSelectedDepartment('all');
                  setSelectedStatus('all');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                Clear filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results summary */}
      {(localSearchQuery || selectedDepartment !== 'all' || selectedStatus !== 'all') && (
        <div className="text-sm text-gray-500">
          Showing {filteredEmployees.length} of {employees.length} employees
          {localSearchQuery && <span> matching "<strong>{localSearchQuery}</strong>"</span>}
          {selectedDepartment !== 'all' && <span> in <strong>{selectedDepartment}</strong></span>}
          {selectedStatus !== 'all' && <span> with status <strong>{selectedStatus}</strong></span>}
        </div>
      )}

      {/* Employee List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEmployees.map((employee) => (
          <Card 
            key={employee.id} 
            className={`hover:shadow-md transition-shadow ${!employee.is_active ? 'opacity-60' : ''}`}
            data-testid={`employee-card-${employee.id}`}
          >
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {employee.profile_picture ? (
                      <img 
                        src={employee.profile_picture} 
                        alt={employee.full_name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${employee.is_active ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        <span className={`font-semibold text-lg ${employee.is_active ? 'text-blue-600' : 'text-gray-400'}`}>
                          {employee.first_name?.[0]}{employee.last_name?.[0]}
                        </span>
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">{employee.full_name}</h3>
                      <p className="text-sm text-gray-500">{employee.employment?.employee_id}</p>
                    </div>
                  </div>
                  {getStatusBadge(employee.employment?.employment_status)}
                </div>

                {/* Details */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Briefcase className="h-4 w-4" />
                    <span>{employee.employment?.job_title}</span>
                  </div>
                  {employee.employment?.department && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Building className="h-4 w-4" />
                      <span>{employee.employment.department}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="h-4 w-4" />
                    <span>{employee.contact?.phone}</span>
                  </div>
                  {employee.contact?.email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="h-4 w-4" />
                      <span className="truncate">{employee.contact.email}</span>
                    </div>
                  )}
                  {/* Line Manager */}
                  <div className="flex items-center gap-2 text-gray-600">
                    <UserCog className="h-4 w-4" />
                    <span className="truncate">
                      {employee.line_manager_name ? (
                        <span className="text-blue-600">{employee.line_manager_name}</span>
                      ) : (
                        <span className="text-gray-400 italic">No manager assigned</span>
                      )}
                    </span>
                  </div>
                </div>

                {/* Special Badges */}
                {employee.special_badges && employee.special_badges.length > 0 && (
                  <div className="pt-2 border-t">
                    <EmployeeBadges badges={employee.special_badges} size="sm" />
                  </div>
                )}

                {/* Employment Info */}
                <div className="pt-2 border-t text-xs text-gray-500">
                  <div className="flex justify-between">
                    <span>{employee.employment?.employment_type}</span>
                    <span>{employee.employment?.work_location}</span>
                  </div>
                  {employee.employment?.hire_date && (
                    <p className="mt-1">
                      Hired: {new Date(employee.employment.hire_date).toLocaleDateString()}
                    </p>
                  )}
                  {!employee.is_active && employee.deactivation_reason && (
                    <p className="mt-1 text-red-500">
                      {employee.deactivation_reason}
                      {employee.deactivation_date && ` - ${new Date(employee.deactivation_date).toLocaleDateString()}`}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t">
                  {/* Badge Management button - for admins */}
                  {(isOfficeAdmin() || isSuperAdmin()) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedEmployee(employee);
                        setShowBadgeModal(true);
                      }}
                      title="Manage Badges"
                      className="text-amber-600 border-amber-200 hover:bg-amber-50"
                      data-testid={`manage-badges-${employee.id}`}
                    >
                      <Award className="h-4 w-4" />
                    </Button>
                  )}
                  {/* Line Manager button - for admins */}
                  {(isOfficeAdmin() || isSuperAdmin()) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openLineManagerModal(employee)}
                      title="Assign Line Manager"
                      data-testid={`assign-manager-${employee.id}`}
                    >
                      <UserCog className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openViewModal(employee)}
                    data-testid={`view-employee-${employee.id}`}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    onClick={() => handleResetPin(employee)}
                    disabled={resettingPinId === employee.id}
                    data-testid={`reset-pin-${employee.id}`}
                    title="Reset PIN"
                  >
                    {resettingPinId === employee.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => openEditModal(employee)}
                    data-testid={`edit-employee-${employee.id}`}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  {employee.is_active ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                      onClick={() => openDeactivateModal(employee)}
                      data-testid={`deactivate-employee-${employee.id}`}
                    >
                      <UserX className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => openActivateModal(employee)}
                      data-testid={`activate-employee-${employee.id}`}
                    >
                      <UserCheck className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty state for filtered results */}
      {filteredEmployees.length === 0 && employees.length > 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No matching employees
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              No employees match your current filters. Try adjusting your search or filters.
            </p>
            <Button 
              variant="outline"
              onClick={() => {
                setLocalSearchQuery('');
                setSelectedDepartment('all');
                setSelectedStatus('all');
              }}
            >
              Clear all filters
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Empty state for no employees at all */}
      {employees.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No employees found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Get started by adding your first employee
            </p>
            <Button onClick={() => setShowAddModal(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add Employee
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add Employee Modal */}
      <AddEmployeeModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onEmployeeAdded={() => {
          fetchEmployees();
        }}
        departments={departments}
      />

      {/* Deactivate Employee Modal */}
      <Dialog open={showDeactivateModal} onOpenChange={setShowDeactivateModal}>
        <DialogContent className="sm:max-w-md" data-testid="deactivate-employee-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-orange-500" />
              Deactivate Employee
            </DialogTitle>
            <DialogDescription>
              This will deactivate the employee&apos;s account and prevent them from logging in.
            </DialogDescription>
          </DialogHeader>
          
          {selectedEmployee && (
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-800">
                  You are about to deactivate <strong>{selectedEmployee.full_name}</strong>
                </p>
                <p className="text-xs text-orange-600 mt-1">
                  {selectedEmployee.employment?.job_title} - {selectedEmployee.employment?.department}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Reason for deactivation *</Label>
                <Select value={deactivationReason} onValueChange={setDeactivationReason}>
                  <SelectTrigger data-testid="deactivation-reason-select">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEACTIVATION_REASONS.map(reason => (
                      <SelectItem key={reason.value} value={reason.value}>
                        <div>
                          <span className="font-medium">{reason.label}</span>
                          <span className="text-xs text-gray-500 ml-2">- {reason.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="effectiveDate">Effective Date</Label>
                <Input
                  id="effectiveDate"
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  data-testid="effective-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="details">Additional Details (optional)</Label>
                <Textarea
                  id="details"
                  value={deactivationDetails}
                  onChange={(e) => setDeactivationDetails(e.target.value)}
                  placeholder="Add any additional notes or details..."
                  rows={3}
                  data-testid="deactivation-details-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeactivateModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeactivate}
              disabled={!deactivationReason || processing}
              data-testid="confirm-deactivate-button"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Deactivate Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate Employee Modal */}
      <Dialog open={showActivateModal} onOpenChange={setShowActivateModal}>
        <DialogContent className="sm:max-w-md" data-testid="activate-employee-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-green-500" />
              Reactivate Employee
            </DialogTitle>
            <DialogDescription>
              This will reactivate the employee&apos;s account and allow them to log in again.
            </DialogDescription>
          </DialogHeader>
          
          {selectedEmployee && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  You are about to reactivate <strong>{selectedEmployee.full_name}</strong>
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {selectedEmployee.employment?.job_title} - {selectedEmployee.employment?.department}
                </p>
              </div>

              {selectedEmployee.deactivation_reason && (
                <div className="p-3 bg-gray-50 rounded-lg text-sm">
                  <p className="text-gray-500">Previous deactivation reason:</p>
                  <p className="font-medium">{selectedEmployee.deactivation_reason}</p>
                  {selectedEmployee.deactivation_details && (
                    <p className="text-gray-600 mt-1">{selectedEmployee.deactivation_details}</p>
                  )}
                </div>
              )}

              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  The employee&apos;s status will be set back to Active and their user account will be enabled.
                </AlertDescription>
              </Alert>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivateModal(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleActivate}
              disabled={processing}
              data-testid="confirm-activate-button"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reactivate Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Employee Modal - Using AddEmployeeModal */}
      <AddEmployeeModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedEmployee(null);
        }}
        onEmployeeAdded={fetchEmployees}
        departments={departments}
        employee={selectedEmployee}
      />

      {/* View Employee Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-employee-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-blue-500" />
              Employee Details
            </DialogTitle>
            <DialogDescription>
              View complete employee information
            </DialogDescription>
          </DialogHeader>
          
          {selectedEmployee && (
            <div className="space-y-6">
              {/* Header with Avatar and QR Code */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-4">
                  {selectedEmployee.profile_picture ? (
                    <img 
                      src={selectedEmployee.profile_picture} 
                      alt={selectedEmployee.full_name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                    />
                  ) : (
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${selectedEmployee.is_active ? 'bg-blue-100' : 'bg-gray-200'}`}>
                      <span className={`font-bold text-2xl ${selectedEmployee.is_active ? 'text-blue-600' : 'text-gray-400'}`}>
                        {selectedEmployee.first_name?.[0]}{selectedEmployee.last_name?.[0]}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-bold">{selectedEmployee.full_name}</h3>
                    <p className="text-gray-600">{selectedEmployee.employment?.job_title}</p>
                    <div className="flex gap-2 mt-1">
                      <Badge className={selectedEmployee.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {selectedEmployee.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant="outline">{selectedEmployee.employment?.employee_id}</Badge>
                    </div>
                  </div>
                </div>
                {/* QR Code Button */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowQRModal(true)}
                  className="border-green-500 text-green-600 hover:bg-green-50 gap-1"
                >
                  <QrCode className="h-4 w-4" />
                  ID Card
                </Button>
              </div>

              {/* Personal Information */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 border-b pb-2">Personal Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Full Name</p>
                    <p className="font-medium">{selectedEmployee.full_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Gender</p>
                    <p className="font-medium">{selectedEmployee.gender || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Date of Birth</p>
                    <p className="font-medium">
                      {selectedEmployee.date_of_birth 
                        ? new Date(selectedEmployee.date_of_birth).toLocaleDateString() 
                        : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">National ID</p>
                    <p className="font-medium">{selectedEmployee.national_id || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 border-b pb-2">Contact Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-gray-500">Phone</p>
                      <p className="font-medium">{selectedEmployee.contact?.phone || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-gray-500">Email</p>
                      <p className="font-medium">{selectedEmployee.contact?.email || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-500">Address</p>
                    <p className="font-medium">
                      {[selectedEmployee.contact?.address, selectedEmployee.contact?.city, selectedEmployee.contact?.po_box]
                        .filter(Boolean).join(', ') || 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Emergency Contact</p>
                    <p className="font-medium">{selectedEmployee.contact?.emergency_contact_name || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Emergency Phone</p>
                    <p className="font-medium">{selectedEmployee.contact?.emergency_contact_phone || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {/* Employment Information */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 border-b pb-2">Employment Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Employee ID</p>
                    <p className="font-medium">{selectedEmployee.employment?.employee_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Department</p>
                    <p className="font-medium">{selectedEmployee.employment?.department || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Position</p>
                    <p className="font-medium">{selectedEmployee.employment?.job_title}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Employment Type</p>
                    <p className="font-medium">{selectedEmployee.employment?.employment_type}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Work Location</p>
                    <p className="font-medium">{selectedEmployee.employment?.work_location}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Hire Date</p>
                    <p className="font-medium">
                      {selectedEmployee.employment?.hire_date 
                        ? new Date(selectedEmployee.employment.hire_date).toLocaleDateString()
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Line Manager</p>
                    <p className="font-medium">
                      {selectedEmployee.line_manager_name || (
                        <span className="text-gray-400 italic">Not assigned</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Deactivation Info (if inactive) */}
              {!selectedEmployee.is_active && selectedEmployee.deactivation_reason && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-red-600 border-b pb-2">Deactivation Information</h4>
                  <div className="p-3 bg-red-50 rounded-lg text-sm">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-500">Reason</p>
                        <p className="font-medium text-red-700">{selectedEmployee.deactivation_reason}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Effective Date</p>
                        <p className="font-medium">
                          {selectedEmployee.deactivation_date 
                            ? new Date(selectedEmployee.deactivation_date).toLocaleDateString()
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                    {selectedEmployee.deactivation_details && (
                      <div className="mt-2">
                        <p className="text-gray-500">Details</p>
                        <p className="font-medium">{selectedEmployee.deactivation_details}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewModal(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setShowViewModal(false);
              openEditModal(selectedEmployee);
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Line Manager Modal - Enhanced */}
      <Dialog open={showLineManagerModal} onOpenChange={(open) => {
        setShowLineManagerModal(open);
        if (!open) setLineManagerSearch('');
      }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="assign-line-manager-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-blue-500" />
              Assign Line Manager
            </DialogTitle>
            <DialogDescription>
              {selectedEmployee && (
                <>Select a line manager for <strong>{selectedEmployee.full_name}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2 flex-1 overflow-hidden flex flex-col">
            {selectedEmployee?.line_manager_name && (
              <Alert className="bg-blue-50 border-blue-200">
                <UserCog className="h-4 w-4" />
                <AlertDescription>
                  Current manager: <strong>{selectedEmployee.line_manager_name}</strong>
                </AlertDescription>
              </Alert>
            )}
            
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, title, department, ID..."
                value={lineManagerSearch}
                onChange={(e) => setLineManagerSearch(e.target.value)}
                className="pl-9"
                data-testid="line-manager-search"
              />
            </div>
            
            {/* Employee Count */}
            <p className="text-xs text-gray-500">
              {getPotentialManagers().length} employee{getPotentialManagers().length !== 1 ? 's' : ''} available
              {lineManagerSearch && ` (filtered from ${employees.filter(e => e.is_active && e.id !== selectedEmployee?.id).length})`}
            </p>

            {/* Employee List */}
            <div className="flex-1 overflow-y-auto space-y-2 border rounded-lg p-2 min-h-[200px] max-h-[350px]">
              {/* Remove Manager Option */}
              <div
                onClick={() => setSelectedLineManager('none')}
                className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                  selectedLineManager === 'none' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="remove-manager-option"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <UserX className="h-5 w-5 text-gray-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-500 italic">No manager (remove)</p>
                    <p className="text-xs text-gray-400">Clear line manager assignment</p>
                  </div>
                </div>
              </div>

              {/* Managers grouped by department */}
              {getPotentialManagers().length > 0 ? (
                <>
                  {/* Group by department */}
                  {getManagerDepartments().map(dept => {
                    const deptManagers = getPotentialManagers().filter(m => m.employment?.department === dept);
                    if (deptManagers.length === 0) return null;
                    
                    return (
                      <div key={dept} className="mb-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 py-1 bg-gray-100 rounded">
                          {dept} ({deptManagers.length})
                        </p>
                        {deptManagers.map(manager => (
                          <div
                            key={manager.id}
                            onClick={() => setSelectedLineManager(manager.id)}
                            className={`p-3 rounded-lg cursor-pointer border mt-1 transition-colors ${
                              selectedLineManager === manager.id 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                            }`}
                            data-testid={`manager-option-${manager.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden">
                                {manager.profile_picture ? (
                                  <img src={manager.profile_picture} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-sm font-medium text-blue-600">
                                    {manager.first_name?.[0]}{manager.last_name?.[0]}
                                  </span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate">{manager.full_name}</p>
                                <p className="text-sm text-gray-600 truncate">{manager.employment?.job_title || 'No title'}</p>
                                <p className="text-xs text-gray-400">
                                  {manager.employment?.employee_id && <span className="mr-2">{manager.employment.employee_id}</span>}
                                  {manager.contact?.phone && <span>{manager.contact.phone}</span>}
                                </p>
                              </div>
                              {selectedLineManager === manager.id && (
                                <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                  
                  {/* Employees without department */}
                  {getPotentialManagers().filter(m => !m.employment?.department).length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 py-1 bg-gray-100 rounded">
                        Unassigned ({getPotentialManagers().filter(m => !m.employment?.department).length})
                      </p>
                      {getPotentialManagers().filter(m => !m.employment?.department).map(manager => (
                        <div
                          key={manager.id}
                          onClick={() => setSelectedLineManager(manager.id)}
                          className={`p-3 rounded-lg cursor-pointer border mt-1 transition-colors ${
                            selectedLineManager === manager.id 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                          }`}
                          data-testid={`manager-option-${manager.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden">
                              {manager.profile_picture ? (
                                <img src={manager.profile_picture} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-sm font-medium text-blue-600">
                                  {manager.first_name?.[0]}{manager.last_name?.[0]}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{manager.full_name}</p>
                              <p className="text-sm text-gray-600 truncate">{manager.employment?.job_title || 'No title'}</p>
                              <p className="text-xs text-gray-400">
                                {manager.employment?.employee_id && <span className="mr-2">{manager.employment.employee_id}</span>}
                                {manager.contact?.phone && <span>{manager.contact.phone}</span>}
                              </p>
                            </div>
                            {selectedLineManager === manager.id && (
                              <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">
                    {lineManagerSearch ? 'No employees match your search' : 'No employees available'}
                  </p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setShowLineManagerModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignLineManager}
              disabled={processing || !selectedLineManager}
              data-testid="confirm-assign-manager"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {selectedLineManager && selectedLineManager !== 'none' ? 'Assign Manager' : 'Remove Manager'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Badge Management Modal */}
      <BadgeManagementModal
        open={showBadgeModal}
        onOpenChange={setShowBadgeModal}
        employee={selectedEmployee}
        onBadgeUpdate={async () => {
          await fetchEmployees();
          // Close the modal after badge update to force a refresh
          // The next time the user opens it, they'll see the updated badges
          setShowBadgeModal(false);
        }}
      />

      {/* QR Code Modal */}
      {selectedEmployee && (
        <EmployeeQRModal 
          open={showQRModal} 
          onClose={() => setShowQRModal(false)} 
          employeeId={selectedEmployee.id}
          employeeName={selectedEmployee.full_name}
        />
      )}

      {/* Debug Modal */}
      <Dialog open={showDebugModal} onOpenChange={setShowDebugModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-orange-600" />
              Employee Visibility Debug
            </DialogTitle>
            <DialogDescription>
              Diagnostic information to troubleshoot employee visibility issues
            </DialogDescription>
          </DialogHeader>
          
          {debugData && (
            <div className="space-y-4 overflow-y-auto max-h-[60vh] pr-2">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-blue-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{debugData.summary?.total_offices || 0}</p>
                  <p className="text-xs text-blue-600">Offices</p>
                </div>
                <div className="bg-green-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{debugData.summary?.total_employees || 0}</p>
                  <p className="text-xs text-green-600">Total Employees</p>
                </div>
                <div className="bg-red-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-700">{debugData.summary?.total_issues || 0}</p>
                  <p className="text-xs text-red-600">Issues</p>
                </div>
                <div className="bg-orange-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-orange-700">{debugData.summary?.offices_with_multiple_admins || 0}</p>
                  <p className="text-xs text-orange-600">Multi-Admin Offices</p>
                </div>
              </div>
              
              {/* Issues Detected */}
              {debugData.issues_detected?.length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>{debugData.issues_detected.length} Issues Detected:</strong>
                    <ul className="list-disc list-inside mt-2 text-sm max-h-32 overflow-y-auto">
                      {debugData.issues_detected.map((issue, idx) => (
                        <li key={idx}>{issue}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
              
              {/* User Info */}
              <div className="bg-blue-50 rounded-lg p-3">
                <h4 className="font-semibold text-blue-800 mb-2">Current User</h4>
                <div className="text-sm grid grid-cols-2 gap-2">
                  <span>Name: {debugData.user_info?.name}</span>
                  <span>Super Admin: {debugData.user_info?.is_super_admin ? 'Yes' : 'No'}</span>
                  <span className="col-span-2">
                    Roles: {debugData.user_info?.roles?.map(r => `${r.role}@${r.office_id || 'none'}`).join(', ')}
                  </span>
                </div>
              </div>
              
              {/* Request Info */}
              <div className="bg-purple-50 rounded-lg p-3">
                <h4 className="font-semibold text-purple-800 mb-2">Request Info</h4>
                <div className="text-sm space-y-1">
                  <p>X-Office-ID Header: <code className="bg-white px-1 rounded">{debugData.request_info?.x_office_id_header || 'NOT SET'}</code></p>
                  <p>Resolved Office ID: <code className="bg-white px-1 rounded">{debugData.request_info?.resolved_office_id || 'NONE'}</code></p>
                  <p>Query Used: <code className="bg-white px-1 rounded">{JSON.stringify(debugData.query_used)}</code></p>
                  <p>Employees Matching Query: <strong>{debugData.employees_matching_query}</strong></p>
                </div>
              </div>
              
              {/* Office Admins by Office */}
              {debugData.office_admins_by_office && Object.keys(debugData.office_admins_by_office).length > 0 && (
                <div className="bg-orange-50 rounded-lg p-3">
                  <h4 className="font-semibold text-orange-800 mb-2">Office Admins by Office</h4>
                  <div className="space-y-2 text-sm">
                    {Object.entries(debugData.office_admins_by_office).map(([officeId, admins]) => (
                      <div key={officeId} className={`p-2 rounded ${admins.length > 1 ? 'bg-red-100 border border-red-300' : 'bg-white'}`}>
                        <span className="font-medium">{officeId}:</span>
                        {admins.length > 1 && <Badge variant="destructive" className="ml-2 text-xs">MULTIPLE ADMINS!</Badge>}
                        <ul className="ml-4 mt-1">
                          {admins.map((admin, idx) => (
                            <li key={idx}>{admin.name} ({admin.phone})</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Offices */}
              <div className="bg-green-50 rounded-lg p-3">
                <h4 className="font-semibold text-green-800 mb-2">Offices in Database ({debugData.offices_in_db?.length})</h4>
                <div className="space-y-1">
                  {debugData.offices_in_db?.map(office => (
                    <div key={office.id} className="flex items-center gap-2 text-sm">
                      <Badge variant={office.is_active !== false && office.status === 'active' ? "default" : "destructive"}>
                        {office.status || 'active'}
                      </Badge>
                      <span className="font-medium">{office.name}</span>
                      <code className="text-xs bg-white px-1 rounded">{office.id}</code>
                      {office.country && <span className="text-gray-500">({office.country})</span>}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Employees by Office */}
              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="font-semibold text-gray-800 mb-2">
                  All Employees in Database ({debugData.all_employees_raw?.length})
                </h4>
                {Object.entries(debugData.employees_by_office || {}).map(([officeId, data]) => (
                  <details key={officeId} className="mb-2 bg-white rounded border">
                    <summary className="px-3 py-2 cursor-pointer hover:bg-gray-50 font-medium">
                      {data.office_name || 'UNKNOWN OFFICE'} 
                      <Badge className="ml-2">{data.employees?.length || 0}</Badge>
                      {officeId === 'null' || officeId === 'undefined' ? (
                        <Badge variant="destructive" className="ml-2">NO OFFICE ID!</Badge>
                      ) : null}
                    </summary>
                    <div className="px-3 py-2 border-t">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left">
                            <th className="pb-1">ID</th>
                            <th className="pb-1">Name</th>
                            <th className="pb-1">Office ID</th>
                            <th className="pb-1">Active</th>
                            <th className="pb-1">User ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.employees?.map(emp => (
                            <tr key={emp.id} className={emp.is_active === false ? 'text-red-600' : ''}>
                              <td className="py-1">{emp.employee_id}</td>
                              <td className="py-1">{emp.name}</td>
                              <td className="py-1">
                                <code className="text-xs">{emp.office_id || 'NONE'}</code>
                              </td>
                              <td className="py-1">
                                {emp.is_active === false ? (
                                  <Badge variant="destructive" className="text-xs">INACTIVE</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs">Active</Badge>
                                )}
                              </td>
                              <td className="py-1">
                                <code className="text-xs">{emp.user_id || 'NONE'}</code>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDebugModal(false)}>
              Close
            </Button>
            <Button onClick={runDebugCheck} disabled={debugLoading}>
              {debugLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Refresh
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIN Reset Confirmation Dialog */}
      <AlertDialog open={!!pinResetTarget} onOpenChange={(open) => !open && setPinResetTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-blue-600" />
              Reset PIN
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-3">
              <p>
                You are about to reset the PIN for <span className="font-semibold text-gray-900">{pinResetTarget?.first_name} {pinResetTarget?.last_name}</span>.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <p className="font-medium mb-1">What will happen:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>PIN will be reset to the last 4 digits of their phone number</li>
                  <li>All active sessions will be terminated</li>
                  <li>They will be required to set a new PIN on next login</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-pin-reset">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmResetPin}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-pin-reset"
            >
              Reset PIN
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Employees;
